import { api } from './api';
import {
  ExportType,
  ExportFilters,
  ExportResponse,
  ExportStatusResponse,
} from '@/hooks/useExport';

function addExportArrayFiltersToParams(
  params: URLSearchParams,
  filterParams: any,
) {
  if (!filterParams) return;

  Object.entries(filterParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      const filtered = value.filter((v) => v && v.trim() !== '');
      filtered.forEach((item: string) => {
        if (
          key === 'products' ||
          key === 'offers' ||
          key === 'affiliates' ||
          key === 'coproducers' ||
          key === 'templates'
        ) {
          const [label, actualValue] = item.split(':::');
          if (actualValue) {
            params.append(`${key}[]`, actualValue);
          } else {
            params.append(`${key}[]`, item);
          }
        } else {
          params.append(`${key}[]`, item);
        }
      });
    }
  });
}

export class ExportService {
  static async startExport(
    type: ExportType,
    filters: ExportFilters,
  ): Promise<ExportResponse> {
    const urlParams = new URLSearchParams();

    if (
      type === 'orders' ||
      type === 'abandoned-carts' ||
      type === 'subscriptions' ||
      type === 'physical-orders'
    ) {
      const { startDate, endDate, dateAxis, filterParams, q } = filters;

      if (startDate && endDate) {
        if (type === 'subscriptions') {
          if (dateAxis === 'canceled') {
            urlParams.append('canceled_start_date', startDate);
            urlParams.append('canceled_end_date', endDate);
          } else if (dateAxis === 'billing') {
            urlParams.append('billing_start_date', startDate);
            urlParams.append('billing_end_date', endDate);
          } else {
            urlParams.append('start_date', startDate);
            urlParams.append('end_date', endDate);
          }
        } else if (type !== 'physical-orders' && dateAxis === 'paid') {
          urlParams.append('paid_start_date', startDate);
          urlParams.append('paid_end_date', endDate);
        } else if (type !== 'physical-orders' && dateAxis === 'refunded') {
          urlParams.append('refunded_start_date', startDate);
          urlParams.append('refunded_end_date', endDate);
        } else {
          urlParams.append('start_date', startDate);
          urlParams.append('end_date', endDate);
        }
      } else {
        if (startDate) urlParams.append('start_date', startDate);
        if (endDate) urlParams.append('end_date', endDate);
      }

      if (filterParams) {
        addExportArrayFiltersToParams(urlParams, filterParams);
      }

      if (q && q.length > 1) {
        urlParams.append('q', q);
      }
    }

    if (type === 'financial') {
      const { startDate, endDate, status, q } = filters;
      if (startDate) urlParams.append('start_date', startDate);
      if (endDate) urlParams.append('end_date', endDate);
      if (status) urlParams.append('status', status);
      if (q && q.length > 1) urlParams.append('q', q);
    }

    if (
      type === 'orders' ||
      type === 'financial' ||
      type === 'subscriptions' ||
      type === 'physical-orders'
    ) {
      const { currency } = filters;
      if (currency) urlParams.append('currency', currency);
    }

    const response = await api.post(
      `/v1/exports/${type}?${urlParams.toString()}`,
    );
    return response.data;
  }

  static async checkExportStatus(
    reportId: string,
  ): Promise<ExportStatusResponse> {
    const response = await api.get(`/v1/exports/${reportId}`);
    return response.data;
  }

  static async cancelExport(reportId: string, reason?: string): Promise<void> {
    await api.delete(`/v1/exports/${reportId}`, {
      data: { reason },
    });
  }
}
